import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Check,
  Phone,
  Mail,
  MessageCircle,
  ScrollText,
  Users,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getProperty, PROPERTIES } from "@/data/properties";
import { BRAND, bookingLink, whatsappLink } from "@/lib/brand";

export default function PropertyDetail() {
  const { slug } = useParams();
  const property = getProperty(slug);
  const [active, setActive] = useState(0);

  if (!property) {
    return (
      <main className="pt-32 pb-24 text-center min-h-[60vh]" data-testid="property-not-found">
        <h1 className="font-serif text-4xl text-brown">Stay not found</h1>
        <Link to="/#stays" className="mt-6 inline-block text-terracotta font-medium">
          ← Back to all stays
        </Link>
      </main>
    );
  }

  const others = PROPERTIES.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`property-page-${property.slug}`} className="pt-[60px] md:pt-[72px] bg-ivory">
      {/* Gallery hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-12">
        <Link
          to="/#stays"
          data-testid="back-to-stays"
          className="inline-flex items-center gap-2 text-sm font-medium text-brown/70 hover:text-terracotta transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> All Stays
        </Link>

        {/* Title block — above the gallery */}
        <Reveal>
          <p data-testid="detail-category" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-terracotta">
            {property.category}
          </p>
          <h1 className="mt-1.5 font-serif text-4xl md:text-5xl font-semibold text-brown">
            {property.name}
          </h1>
          <p className="mt-2 inline-flex items-center gap-2 text-brown/60">
            <MapPin className="h-4 w-4 text-gold" /> {property.location}
          </p>
        </Reveal>

        <div className="mt-6 grid gap-4 md:grid-cols-[1.6fr_1fr]">
          <motion.div
            key={active}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl overflow-hidden aspect-[4/3] md:aspect-auto md:h-[460px]"
          >
            <img
              src={property.gallery[active]}
              alt={property.name}
              className="h-full w-full object-cover"
            />
          </motion.div>
          <div className="grid grid-cols-4 md:grid-cols-2 gap-3 md:gap-4">
            {property.gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`rounded-xl overflow-hidden aspect-square md:aspect-auto md:h-[110px] transition-all duration-300 ${
                  active === i ? "ring-2 ring-terracotta" : "opacity-75 hover:opacity-100"
                }`}
              >
                <img src={img} alt={`${property.name} ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Cost per night + Book Now — below the gallery */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white p-5 md:p-6 shadow-[0_8px_30px_rgba(74,49,37,0.07)]">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brown/45">
              Cost per night
            </p>
            <p data-testid="detail-cost" className="mt-1 font-serif text-3xl font-semibold text-brown">
              {property.costLabel}
            </p>
          </div>
          <a
            href={bookingLink(property.name)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detail-book-now-top"
            className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-base font-medium text-white hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300"
          >
            <MessageCircle className="h-5 w-5" /> Book Now
          </a>
        </div>
      </section>

      {/* Accommodation options — only for properties that offer them */}
      {property.options && property.options.length > 0 && (
        <section
          data-testid="options-section"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10"
        >
          <Reveal>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-brown">
              {property.optionsTitle}
            </h2>
          </Reveal>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {property.options.map((opt, i) => (
              <Reveal key={opt.name} delay={i * 0.08}>
                <div
                  data-testid={`option-card-${i}`}
                  className="h-full flex flex-col rounded-2xl bg-white p-5 shadow-[0_8px_30px_rgba(74,49,37,0.06)]"
                >
                  <h3 className="font-serif text-xl font-semibold text-brown">{opt.name}</h3>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-terracotta">
                    <Users className="h-3.5 w-3.5" /> {opt.capacity}
                  </p>
                  <p className="mt-3 text-sm text-brown/70 leading-relaxed flex-1">
                    {opt.description}
                  </p>
                  <a
                    href={whatsappLink(opt.message)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`option-book-${i}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-medium text-white hover:bg-terracotta-dark transition-colors duration-300"
                  >
                    <MessageCircle className="h-4 w-4" /> {opt.buttonText}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid gap-12 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <Reveal delay={0.14}>
            <p className="text-brown/80 leading-relaxed text-lg max-w-2xl">
              {property.description}
            </p>
          </Reveal>

          {/* Amenities */}
          <Reveal delay={0.18}>
            <h2 className="mt-12 font-serif text-2xl font-semibold text-brown">Amenities</h2>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="amenities-list">
              {property.amenities.map((a) => (
                <li key={a} className="flex items-center gap-3 text-brown/80">
                  <span className="h-6 w-6 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-terracotta" />
                  </span>
                  {a}
                </li>
              ))}
            </ul>
          </Reveal>

          {/* House rules */}
          <Reveal delay={0.22}>
            <h2 className="mt-12 font-serif text-2xl font-semibold text-brown flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-gold" /> House Rules
            </h2>
            <ul className="mt-5 space-y-2.5" data-testid="house-rules-list">
              {property.houseRules.map((rule) => (
                <li key={rule} className="flex items-start gap-3 text-brown/75">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                  {rule}
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Location / Map */}
          <Reveal delay={0.26}>
            <h2 className="mt-12 font-serif text-2xl font-semibold text-brown">Location</h2>
            <div className="mt-5 rounded-2xl overflow-hidden aspect-video shadow-[0_8px_30px_rgba(74,49,37,0.08)]">
              <iframe
                title={`Map of ${property.name}`}
                src={property.mapsEmbed || BRAND.mapsEmbed}
                loading="lazy"
                className="h-full w-full border-0"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          {/* Important Distances */}
          <Reveal delay={0.3}>
            <h2 className="mt-12 font-serif text-2xl font-semibold text-brown">Important Distances</h2>
            <ul className="mt-5 max-w-md space-y-3" data-testid="detail-distances">
              {property.distances.map((d) => (
                <li key={d.place} className="flex items-baseline justify-between gap-6 border-b border-brown/10 pb-3 text-sm">
                  <span className="text-brown/70">{d.place}</span>
                  <span className="font-medium text-brown whitespace-nowrap">{d.km}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Sticky booking card */}
        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="rounded-2xl bg-white p-7 shadow-[0_12px_40px_rgba(74,49,37,0.10)]">
            <h3 className="font-serif text-2xl font-semibold text-brown">Book this stay</h3>
            <p className="mt-2 text-sm text-brown/65 leading-relaxed">
              Message us on WhatsApp for availability and pricing — we usually reply within minutes.
            </p>
            <a
              href={bookingLink(property.name)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-book-now"
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-base font-medium text-white hover:bg-terracotta-dark transition-colors duration-300"
            >
              <MessageCircle className="h-5 w-5" /> Book Now
            </a>
            <div className="mt-5 space-y-3 border-t border-brown/10 pt-5 text-sm">
              <a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-3 text-brown/75 hover:text-terracotta transition-colors">
                <Phone className="h-4 w-4 text-gold" /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-brown/75 hover:text-terracotta transition-colors break-all">
                <Mail className="h-4 w-4 text-gold" /> {BRAND.email}
              </a>
            </div>
          </div>
        </aside>
      </section>

      {/* Other stays */}
      <section className="bg-[#F7F2EA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-brown">Other stays you may like</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/stays/${p.slug}`}
                data-testid={`related-${p.slug}`}
                className="group block rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgba(74,49,37,0.06)]"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.cover} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-terracotta">{p.category}</p>
                  <h3 className="mt-1 font-serif text-xl font-semibold text-brown">{p.name}</h3>
                  <p className="mt-1 text-sm text-brown/60">Starting from ₹{p.price}/night</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
